import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useUserPreferences, useCurrencies } from "@/hooks/useUserPreferences";
import { DollarSign, Globe, Wallet } from "lucide-react";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";

const COMMON_LOCALES = [
  { code: "en-US", name: "English (US)", example: "1,234.56" },
  { code: "en-GB", name: "English (UK)", example: "1,234.56" },
  { code: "de-DE", name: "German", example: "1.234,56" },
  { code: "fr-FR", name: "French", example: "1 234,56" },
  { code: "es-ES", name: "Spanish", example: "1.234,56" },
  { code: "it-IT", name: "Italian", example: "1.234,56" },
  { code: "pt-BR", name: "Portuguese (Brazil)", example: "1.234,56" },
  { code: "nl-NL", name: "Dutch", example: "1.234,56" },
  { code: "pl-PL", name: "Polish", example: "1 234,56" },
  { code: "ru-RU", name: "Russian", example: "1 234,56" },
  { code: "ja-JP", name: "Japanese", example: "1,234.56" },
  { code: "zh-CN", name: "Chinese (Simplified)", example: "1,234.56" },
  { code: "ko-KR", name: "Korean", example: "1,234.56" },
  { code: "ar-SA", name: "Arabic", example: "١٬٢٣٤٫٥٦" },
  { code: "hi-IN", name: "Hindi", example: "1,234.56" },
];

interface PaymentSource {
  value: string;
  label: string;
  isWallet?: boolean;
  parentAccount?: string;
}

export const CurrencySettings = () => {
  const { preferences, loading, updateCurrency, updateLocale, updateDefaultAccount, locale } = useUserPreferences();
  const currencies = useCurrencies();
  const [paymentSources, setPaymentSources] = useState<PaymentSource[]>([]);

  useEffect(() => {
    const fetchPaymentSources = async () => {
      try {
        const [accountsRes, walletsRes] = await Promise.all([
          supabase.from("accounts").select("id, name").order("created_at", { ascending: true }),
          supabase.from("wallets").select("id, account_id, name").order("created_at", { ascending: true })
        ]);

        if (accountsRes.error) throw accountsRes.error;
        
        const accounts = accountsRes.data || [];
        const wallets = walletsRes.data || [];
        
        const sources: PaymentSource[] = [];
        
        accounts.forEach(acc => {
          sources.push({ value: acc.name, label: acc.name });
          
          const accountWallets = wallets.filter(w => w.account_id === acc.id);
          accountWallets.forEach(wallet => {
            sources.push({
              value: `${acc.name} → ${wallet.name}`,
              label: `${wallet.name}`,
              isWallet: true,
              parentAccount: acc.name
            });
          });
        });

        setPaymentSources(sources);
      } catch (error) {
        console.error("Error fetching payment sources:", error);
      }
    };

    fetchPaymentSources();
  }, []);

  if (loading) {
    return (
      <Card className="bg-card/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Preferences</CardTitle>
          <CardDescription className="text-xs">Loading...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="bg-card/50">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Globe className="h-4 w-4" />
          Regional Settings
        </CardTitle>
        <CardDescription className="text-xs">
          Currency and number formatting preferences
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label className="text-sm flex items-center gap-2">
            <DollarSign className="h-3.5 w-3.5" />
            Currency
          </Label>
          <Select value={preferences?.currency || "USD"} onValueChange={updateCurrency}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select currency" />
            </SelectTrigger>
            <SelectContent>
              {currencies.map((currency) => (
                <SelectItem key={currency.code} value={currency.code}>
                  <span className="flex items-center gap-2">
                    <span className="font-mono">{currency.symbol}</span>
                    <span>{currency.code}</span>
                    <span className="text-muted-foreground">- {currency.name}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-sm">Number Format</Label>
          <Select value={locale} onValueChange={updateLocale}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select number format" />
            </SelectTrigger>
            <SelectContent>
              {COMMON_LOCALES.map((loc) => (
                <SelectItem key={loc.code} value={loc.code}>
                  <span className="flex items-center justify-between gap-4 w-full">
                    <span>{loc.name}</span>
                    <span className="text-muted-foreground font-mono text-xs">{loc.example}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            This affects how numbers are displayed and entered throughout the app
          </p>
        </div>

        <div className="space-y-2 pt-2 border-t">
          <Label className="text-sm flex items-center gap-2">
            <Wallet className="h-3.5 w-3.5" />
            Default Account
          </Label>
          <Select 
            value={preferences?.default_account || "none"} 
            onValueChange={(value) => updateDefaultAccount(value === "none" ? null : value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select default account" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                <span className="text-muted-foreground">No default (use first account)</span>
              </SelectItem>
              {paymentSources.map((source) => (
                <SelectItem 
                  key={source.value} 
                  value={source.value}
                  className={source.isWallet ? "pl-6" : "font-medium"}
                >
                  {source.isWallet ? (
                    <span className="flex items-center gap-1">
                      <span className="text-muted-foreground text-xs">└</span>
                      {source.label}
                    </span>
                  ) : (
                    source.label
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            This account will be pre-selected when adding new transactions
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
